import { format, parseISO } from 'date-fns';
import { Check, X, ChevronLeft, ChevronRight, Loader2, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

export interface RecurrenceOccurrence {
  datetime: string;
  ok: boolean;
  reason: string | null;
  details?: {
    booking_id?: number;
    booking_public_id?: string;
    service_name?: string;
    customer_name?: string;
    note?: string;
    source?: string;
    customer_email?: string;
    can_create_availability?: boolean;
  };
}

interface RecurrencePreviewListProps {
  occurrences: RecurrenceOccurrence[];
  className?: string;
  onShift?: (index: number, direction: -1 | 1) => void;
  shiftingIndex?: number | null;
  adhocSet?: Set<string>;
  onToggleAdhoc?: (datetime: string) => void;
}

export function RecurrencePreviewList({
  occurrences,
  className,
  onShift,
  shiftingIndex,
  adhocSet,
  onToggleAdhoc,
}: RecurrencePreviewListProps) {
  const { t } = useTranslation('bookings');

  const reasonLabel = (occ: RecurrenceOccurrence) => {
    if (!occ.reason) return null;
    const base = t(`recurring.preview.reasons.${occ.reason}`, occ.reason);
    const d = occ.details || {};
    switch (occ.reason) {
      case 'conflict':
        if (d.service_name || d.customer_name) {
          return `${base} — ${[d.service_name, d.customer_name].filter(Boolean).join(' · ')}`;
        }
        return base;
      case 'time_off':
        return d.note ? `${base}: ${d.note}` : base;
      case 'calendar_block':
        return d.source ? `${base}: ${d.source}` : base;
      case 'customer_busy':
        return d.customer_email ? `${base}: ${d.customer_email}` : base;
      default:
        return base;
    }
  };

  return (
    <ul className={cn('rounded-2xl border border-line bg-surface divide-y divide-line', className)}>
      {occurrences.map((occ, idx) => {
        const dt = parseISO(occ.datetime);
        const isShifting = shiftingIndex === idx;
        const adhocEligible =
          !occ.ok && occ.reason === 'no_availability' && occ.details?.can_create_availability;
        const adhocOn = adhocSet?.has(occ.datetime) ?? false;
        const showAsCreate = adhocEligible && adhocOn;
        const dotClass = showAsCreate
          ? 'bg-amber-100 text-amber-700'
          : occ.ok
            ? 'bg-success/15 text-success'
            : 'bg-danger/15 text-danger';
        const Icon = showAsCreate ? Sparkles : occ.ok ? Check : X;

        return (
          <li
            key={`${occ.datetime}-${idx}`}
            className="flex items-center gap-3 px-4 py-3"
            data-testid={`preview-row-${idx}`}
            data-adhoc={showAsCreate ? 'true' : 'false'}
          >
            <span
              className={cn(
                'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-pill',
                dotClass,
              )}
            >
              <Icon className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-body-sm font-semibold tabular-nums text-ink">
                {format(dt, 'EEE d MMM yyyy · HH:mm')}
              </p>
              {!occ.ok && !showAsCreate && (
                <p
                  className="mt-0.5 text-body-xs text-danger"
                  data-testid={`preview-reason-${occ.reason}`}
                >
                  {reasonLabel(occ)}
                </p>
              )}
              {showAsCreate && (
                <p className="mt-0.5 text-body-xs text-amber-700">
                  {t(
                    'recurring.preview.willCreateAvailability',
                    'Availability will be created for this slot',
                  )}
                </p>
              )}
            </div>
            {adhocEligible && onToggleAdhoc && (
              <label className="flex items-center gap-1.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="rounded border-line text-amber-600 focus:ring-amber-500"
                  checked={adhocOn}
                  onChange={() => onToggleAdhoc(occ.datetime)}
                  data-testid={`adhoc-toggle-${idx}`}
                />
                <span className="text-body-xs text-soft">
                  {t('recurring.preview.createAvailability', 'Create availability')}
                </span>
              </label>
            )}
            {onShift && !occ.ok && occ.reason !== 'past' && !adhocOn && (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={isShifting}
                  onClick={() => onShift(idx, -1)}
                  className="rounded-md border border-line p-1 hover:bg-surface-2 disabled:opacity-50"
                  title={t('recurring.preview.shiftBack', 'Shift one week earlier')}
                  data-testid={`shift-back-${idx}`}
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  disabled={isShifting}
                  onClick={() => onShift(idx, 1)}
                  className="rounded-md border border-line p-1 hover:bg-surface-2 disabled:opacity-50"
                  title={t('recurring.preview.shiftForward', 'Shift one week later')}
                  data-testid={`shift-forward-${idx}`}
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
                {isShifting && <Loader2 className="h-3.5 w-3.5 animate-spin text-soft" />}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
